"use client";

import { useEffect } from "react";

import { ReferralSchedulingRecommendation } from "@/src/components/comms/referral-scheduling-recommendation";
import { CopyableContent } from "@/src/components/copyable-content";
import { EhrInstructionsCard } from "@/src/components/ehr-instructions-card";
import ErrorAlert from "@/src/components/error-alert";
import { AthenaReferralCards } from "@/src/components/tasks/athena-referral-cards";
import { SyncIncompleteActions } from "@/src/components/tasks/sync-incomplete-actions";
import { useProvidersForDiagnostics } from "@/src/lib/data-fetchers/diagnosis-providers-fetchers";
import { useLocationProvidersInstructions } from "@/src/lib/data-fetchers/location-providers-fetchers";
import { logger } from "@/src/lib/logging/logger";
import { tryGetEhrSystemId } from "@/src/lib/resolve-ehr-ids";
import { getFormattedAddress } from "@/src/models/address";
import { Appointment } from "@/src/models/appointment";
import { Coverage } from "@/src/models/coverage";
import { Referral } from "@/src/models/documents/referral";
import { EHRDocument } from "@/src/models/ehr-document";
import { Patient } from "@/src/models/patient";
import { getPersonName } from "@/src/models/person";
import { getPractitionerNameWithCredentials, Practitioner } from "@/src/models/practitioner";
import { type LocationProvidersInstructionsEntry } from "@/src/requests/location-providers-instructions";

interface AthenaData {
  patientDetails: Patient | null;
  coverages: Coverage[];
  careTeam: Practitioner[];
  appointmentReminders: Appointment[];
  bookedAppointments: Appointment[];
  document: EHRDocument | null;
  expectedDocument: EHRDocument | null;
  documentPatientId?: string | null;
}

interface AthenaReferralInstructionsProps {
  referral: Referral;
  departmentId?: string;
  athenaData: AthenaData | null;
  customInstructions: string | null;
  showSchedulingRecommendation?: boolean;
  taskId: string;
}

type DisplayProvider = { text: string; preferred?: boolean };

type DisplayRow = {
  key: string;
  locationLabel: string;
  departmentName: string;
  providerGroup: string;
  providers: DisplayProvider[];
};

function buildReceivingLocationRows(params: {
  isPending: boolean;
  entries: LocationProvidersInstructionsEntry[];
  hasReceivingLocation: boolean;
  receivingLocationAddress: string;
  providersForDiagnostics: { name: string; isPreferred?: boolean }[] | undefined;
}): DisplayRow[] {
  const { isPending, entries, hasReceivingLocation, receivingLocationAddress, providersForDiagnostics } = params;

  if (isPending) {
    return [];
  }

  const diagnosticProviders: DisplayProvider[] = (providersForDiagnostics ?? []).map((provider) => ({
    text: provider.name,
    preferred: provider.isPreferred,
  }));

  if (entries.length === 0) {
    return diagnosticProviders.length === 0
      ? []
      : [
          {
            key: "diagnosis-providers",
            locationLabel: receivingLocationAddress,
            departmentName: "N/A",
            providerGroup: "N/A",
            providers: diagnosticProviders,
          },
        ];
  }

  return entries.map((entry) => ({
    key: entry.receivingLocationId,
    locationLabel: hasReceivingLocation ? receivingLocationAddress : entry.department?.name || "N/A",
    departmentName: entry.department?.name || "N/A",
    providerGroup: entry.department?.providerGroup || "N/A",
    providers:
      entry.providers.length > 0
        ? entry.providers.map((text) => ({ text }))
        : hasReceivingLocation
          ? diagnosticProviders
          : [],
  }));
}

export function AthenaReferralInstructions({
  referral,
  departmentId,
  athenaData,
  customInstructions,
  showSchedulingRecommendation = false,
  taskId,
}: AthenaReferralInstructionsProps) {
  useEffect(() => {
    logger.info("Data from Athena", { athenaData });
  }, [athenaData]);

  const receivingLocationId = referral.receivingLocation?.id ?? null;
  const { data: locationProvidersData, isPending } = useLocationProvidersInstructions(receivingLocationId);

  const { data: providersForDiagnostics } = useProvidersForDiagnostics(referral.diagnosisList || [], departmentId);

  const receivingLocationAddress = referral.receivingLocation?.address
    ? getFormattedAddress(referral.receivingLocation.address) || "N/A"
    : "N/A";

  const receivingLocationRows = buildReceivingLocationRows({
    isPending,
    entries: locationProvidersData ?? [],
    hasReceivingLocation: receivingLocationId != null,
    receivingLocationAddress,
    providersForDiagnostics,
  });
  const showCatalogBanner = receivingLocationId == null && (locationProvidersData?.length ?? 0) > 0;

  const patientName = getPersonName(athenaData?.patientDetails, true, true) || "N/A";
  const ehrId = (athenaData?.patientDetails && tryGetEhrSystemId(athenaData.patientDetails)) || "N/A";
  const documentId = athenaData?.document?.ehrId ?? "N/A";
  const referringProviderName = referral.referringProvider
    ? getPractitionerNameWithCredentials(referral.referringProvider)
    : "N/A";
  const referringProviderNPI = referral.referringProvider?.npiNumber || "N/A";
  const referringLocationAddress = referral.referringLocation?.address
    ? getFormattedAddress(referral.referringLocation.address) || "N/A"
    : "N/A";
  // Only rendered when the document carried one, so tenants that leave
  // referralInformation.authorizationNumber disabled see no empty row.
  const authorizationNumber = referral.authorizationNumber?.trim() || null;

  return (
    <div className="space-y-8 text-foreground">
      {!athenaData && <ErrorAlert compact={true} message="No Athena data available" />}

      <EhrInstructionsCard>
        <div className="text-sm text-muted-foreground">
          <h3 className="text-md font-semibold text-center mb-4">Athena Referral Instructions</h3>

          <div className="grid grid-cols-3 gap-y-4 mb-6">
            <div>
              <span className="font-medium text-foreground">Patient Name: </span>
              <CopyableContent text={patientName} />
            </div>
            <div>
              <span className="font-medium text-foreground">EHR ID: </span>
              <CopyableContent text={ehrId} />
            </div>
            <div>
              <span className="font-medium text-foreground">Document ID: </span>
              <CopyableContent text={documentId} />
            </div>
            {athenaData?.documentPatientId && (
              <div>
                <span className="font-medium text-foreground">Document Patient ID: </span>
                <CopyableContent text={athenaData.documentPatientId} />
              </div>
            )}
            <div className={athenaData?.documentPatientId ? "" : "col-start-2"}>
              <span className="font-medium text-foreground">Referring Provider: </span>
              <CopyableContent text={referringProviderName} />
            </div>
            <div>
              <span className="font-medium text-foreground">NPI: </span>
              <CopyableContent text={referringProviderNPI} />
            </div>
            <div className="col-span-3">
              <span className="font-medium text-foreground">Referring Location: </span>
              <CopyableContent text={referringLocationAddress} />
            </div>
            {authorizationNumber && (
              <div className="col-span-3">
                <span className="font-medium text-foreground">Authorization Number: </span>
                <CopyableContent text={authorizationNumber} />
              </div>
            )}
          </div>

          {receivingLocationRows.length > 0 && (
            <div className="mb-4 my-2">
              <h3 className="font-semibold text-foreground my-2">Receiving Location and Providers:</h3>
              {showCatalogBanner && (
                <p className="text-xs text-muted-foreground mb-2">All locations — no receiving location</p>
              )}
              <div className="overflow-x-auto">
                <table className="w-full border border-border rounded-lg">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-3 font-medium text-foreground">Receiving Location</th>
                      <th className="text-left p-3 font-medium text-foreground">Department</th>
                      <th className="text-left p-3 font-medium text-foreground">Provider Group</th>
                      <th className="text-left p-3 font-medium text-foreground">Providers</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receivingLocationRows.map((row) => (
                      <tr key={row.key} className="border-t border-border">
                        <td className="p-3 text-sm">
                          <span className="text-muted-foreground">
                            <CopyableContent text={row.locationLabel} />
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          <span className="text-muted-foreground">
                            <CopyableContent text={row.departmentName} />
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          <span className="text-muted-foreground">
                            <CopyableContent text={row.providerGroup} />
                          </span>
                        </td>
                        <td className="p-3 text-sm">
                          <div className="space-y-1">
                            {row.providers.map((provider) => (
                              <div key={provider.text}>
                                <span
                                  className={
                                    provider.preferred ? "font-semibold text-muted-foreground" : "text-muted-foreground"
                                  }
                                >
                                  <CopyableContent text={provider.text} />
                                </span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {showSchedulingRecommendation && <ReferralSchedulingRecommendation referral={referral} />}

          {customInstructions && <p className="text-sm mt-4">{customInstructions}</p>}

          <div className="mt-4">
            <SyncIncompleteActions taskId={taskId} />
          </div>
        </div>
      </EhrInstructionsCard>

      <AthenaReferralCards referral={referral} athenaData={athenaData} taskId={taskId} />
    </div>
  );
}
